import type { ReactElement } from "react";
// Shared TYPE from the backend/foundation contract. Type-only import: web may
// consume @avos/foundation for shared types only (enforced by the web boundary
// lint rule), never runtime values. The UserMessage component can therefore
// carry a backend error envelope straight through without duplicating its shape.
import type { ErrorEnvelope } from "@avos/foundation";

/**
 * Accessible error/status presentation component (Requirement 26).
 *
 * `UserMessage` is the single place the frontend turns a machine-readable
 * condition (a backend {@link ErrorEnvelope}, or a locally-built
 * {@link UserMessageModel}) into human-facing text. It enforces the SRS
 * what/why/next-action contract so a user is never left with only a code:
 *
 *   - WHAT happened  — always rendered (Req 26.1).
 *   - WHY it happened — always rendered (Req 26.1).
 *   - NEXT action     — always rendered; when the caller has no user step, it
 *                       DEFAULTS to "No action required — contact support or
 *                       wait for the condition to clear." (Req 26.2).
 *   - a raw diagnostic `code` is NEVER shown alone; when present it appears only
 *     as supplementary technical detail alongside the what/why/next (Req 26.3).
 *   - the same what/why/next text is exposed to Assistive_Technology via a live
 *     region (`role="alert"` for errors, `role="status"`/`aria-live` for status)
 *     and is plain, programmatically-determinable text (Req 26.4).
 *   - an error is signalled by at least one NON-COLOR means: a text label
 *     ("Error") plus an icon glyph, not color alone (Req 26.5).
 *
 * The component is SSR-friendly: it is a pure function of its props with no
 * clock/DOM/global reads, so it renders identically under
 * `react-dom/server`'s `renderToString` and on the client after hydration.
 */

/**
 * The default NEXT-action text used when a User_Message has no user-actionable
 * step (Req 26.2). Exported so screens, the mapper, and the property test
 * (Property 38 / task 14.7) can reference the exact string rather than
 * duplicating the literal.
 */
export const NO_ACTION_NEXT =
  "No action required — contact support or wait for the condition to clear." as const;

/** Whether a User_Message reports an error or an informational status. */
export type UserMessageSeverity = "error" | "status";

/**
 * The what/why/next model a {@link UserMessage} renders.
 *
 * This is the structural contract screens build (or that {@link toUserMessage}
 * derives from a backend envelope). `what` and `why` are the human-readable
 * statements; `next` is optional at the model level — an empty/absent `next`
 * is DEFAULTED to {@link NO_ACTION_NEXT} at render time (Req 26.2), so callers
 * never have to supply boilerplate guidance. `code` is optional supplementary
 * technical detail; it is never presented alone (Req 26.3).
 */
export interface UserMessageModel {
  /** WHAT happened — a human-readable statement (Req 26.1). Required. */
  readonly what: string;
  /** WHY it happened — a human-readable statement (Req 26.1). Required. */
  readonly why: string;
  /**
   * The NEXT action available to the user (Req 26.1). Optional: when empty or
   * omitted the component substitutes {@link NO_ACTION_NEXT} (Req 26.2).
   */
  readonly next?: string;
  /**
   * An optional supplementary diagnostic code. Shown only as accompanying
   * technical detail next to the what/why/next — never as the sole content
   * (Req 26.3).
   */
  readonly code?: string;
  /**
   * An optional correlation id shown as supplementary technical detail (helps a
   * user quote it to support). Never presented alone.
   */
  readonly requestId?: string;
}

/** Props for {@link UserMessage}. */
export interface UserMessageProps {
  /** The what/why/next model to present. */
  readonly message: UserMessageModel;
  /**
   * Whether this message reports an error or a status. Defaults to `"error"`.
   * Errors use `role="alert"` (assertive) and a visible non-color error signal
   * (icon + "Error" text); statuses use `role="status"` (polite).
   */
  readonly severity?: UserMessageSeverity;
}

/**
 * Resolve the NEXT-action text, defaulting to {@link NO_ACTION_NEXT} when the
 * model supplies no user step (empty, whitespace-only, or omitted) — Req 26.2.
 */
export function resolveNextAction(next: string | undefined): string {
  if (next === undefined || next.trim() === "") {
    return NO_ACTION_NEXT;
  }
  return next;
}

/**
 * Map a backend {@link ErrorEnvelope} to a {@link UserMessageModel}.
 *
 * The envelope's `message`/`detail`/`next` populate what/why/next respectively;
 * an empty `next` is left empty here so {@link resolveNextAction} (invoked at
 * render) applies the Req 26.2 default in one place. The `code` and `request_id`
 * are carried as supplementary technical detail only (Req 26.3).
 *
 * Pure and side-effect free, so it is SSR-safe and unit/property testable.
 */
export function toUserMessage(envelope: ErrorEnvelope): UserMessageModel {
  const { code, message, detail, next, request_id } = envelope.error;
  return {
    what: message,
    why: detail,
    ...(next !== undefined && next.trim() !== "" ? { next } : {}),
    code,
    requestId: request_id,
  };
}

/**
 * The accessible User_Message component (Req 26).
 *
 * Rendering rules (all enforced here so screens cannot violate the contract):
 *   - what, why, and next are ALWAYS rendered; next is defaulted (Req 26.1/26.2).
 *   - the container is a live region (`role="alert"` for errors, `role="status"`
 *     for status) carrying the same textual what/why/next, so Assistive_Technology
 *     announces the identical information (Req 26.4).
 *   - for errors, a NON-COLOR signal is present: an icon glyph (aria-hidden, so it
 *     is not double-announced) plus a visible "Error" text label (Req 26.5).
 *   - any `code`/`requestId` renders only as supplementary technical detail
 *     alongside the human-readable text — never alone (Req 26.3).
 */
export function UserMessage({
  message,
  severity = "error",
}: UserMessageProps): ReactElement {
  const isError = severity === "error";
  const next = resolveNextAction(message.next);
  const role = isError ? "alert" : "status";
  const ariaLive = isError ? "assertive" : "polite";

  return (
    <div
      role={role}
      aria-live={ariaLive}
      data-testid="user-message"
      data-severity={severity}
      className={isError ? "user-message user-message--error" : "user-message"}
    >
      {isError ? (
        <p className="user-message__signal" data-testid="user-message-signal">
          {/*
            Non-color error signal (Req 26.5): an icon glyph AND a text label.
            The glyph is decorative for AT (aria-hidden) because the adjacent
            "Error" text already conveys the meaning as programmatic text.
          */}
          <span className="user-message__icon" aria-hidden="true">
            ⚠
          </span>
          <span className="user-message__severity-label">Error</span>
        </p>
      ) : null}
      <p className="user-message__what" data-testid="user-message-what">
        {message.what}
      </p>
      <p className="user-message__why" data-testid="user-message-why">
        {message.why}
      </p>
      <p className="user-message__next" data-testid="user-message-next">
        {next}
      </p>
      {message.code !== undefined || message.requestId !== undefined ? (
        <p
          className="user-message__technical"
          data-testid="user-message-technical"
        >
          {/*
            Supplementary technical detail only. The what/why/next above are the
            primary content, so a code is never presented alone (Req 26.3).
          */}
          {message.code !== undefined ? (
            <span className="user-message__code" data-testid="user-message-code">
              {message.code}
            </span>
          ) : null}
          {message.requestId !== undefined ? (
            <span className="user-message__request-id">
              {message.requestId}
            </span>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
